import { ContactKind, CurrentUserInputModel } from '@ail/customers-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { OIDC_CLIENT } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { FrontendSettings } from '../../helpers/FrontendSettings'
import { useAuth } from '../../hooks/useAuth'
import { useGetCurrentUserQuery, useGetTenantQuery, usePatchCurrentUserMutation } from '../../redux/api/customerApi'
import { useGetUserSettingsQuery, usePutUserSettingsMutation } from '../../redux/api/notificationsApi'
import { langLocaleMap, langTranslationMap } from '../../types/constants'
import { FrontendSettingsKeys } from '../../types/enums'
import {
  buildSettingsInputModel,
  DefaultChannels,
  NotificationChannelsState,
} from '../AdminPanel/UsersAdminPanel/UserEdit/NotificationSettings/NotificationSettingsUtils'
import { UserNotificationSettings } from './UserNotificationSettings'
import './UserPreferences.scss'
import UserRolesAndPermissions from './UserRolesAndPermissions'

const PROMPTS = {
  YouMayOptOut2: 'You may choose to prevent this website from aggregating and analyzing the actions you take here.',
  YouMayOptOut3:
    'Doing so will protect your privacy, but will also prevent the owner from learning from your actions and creating a better experience for you and other users.',
  UncheckToOptOut: 'Check this box to opt-out.',
}

export default function UserPreferencesFunctional() {
  const { t, i18n } = useTranslation('common')
  const history = useHistory()
  const { user } = useAuth()
  const { currentData: tenant } = useGetTenantQuery(user?.tenant ?? skipToken)
  const [form, setForm] = useState<CurrentUserInputModel>({})
  const [selectedLang, setSelectedLang] = useState(i18n.language)
  const [consent, setConsent] = useState(true)
  const hasProfile = user && !user.isInternalUser
  const { currentData: currentUser } = useGetCurrentUserQuery(hasProfile ? undefined : skipToken)
  const [patchCurrentUser] = usePatchCurrentUserMutation()
  useEffect(() => {
    setForm({
      phoneNumber: currentUser?.contacts.find((c) => c.kind === ContactKind.PhoneNumber)?.value || '',
      mobilePhoneNumber: currentUser?.contacts.find((c) => c.kind === ContactKind.MobileNumber)?.value || '',
      enable2Fa: currentUser?.requiresCodeAuthentication ?? false,
    })
  }, [currentUser])

  const displayNameParts = hasProfile ? currentUser?.displayName?.split(' ') : user?.displayName?.split(' ')
  const firstName = displayNameParts?.length ? displayNameParts[0] : displayNameParts
  const lastName = displayNameParts && displayNameParts.length > 1 ? displayNameParts[displayNameParts.length - 1] : ''
  //#region User Notification Setting
  const canManageNotifs = hasProfile && !user?.isGuest && !!user.permissions.length

  const { currentData: userNotificationsSettings } = useGetUserSettingsQuery(canManageNotifs ? undefined : skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const [putNotificationSettings] = usePutUserSettingsMutation()
  const [channels, setChannels] = useState<NotificationChannelsState>(DefaultChannels)
  const [originalChannels, setOriginalChannels] = useState<NotificationChannelsState>(DefaultChannels)
  useEffect(() => {
    if (!userNotificationsSettings) return
    const seeded: NotificationChannelsState = {
      enabledChannelsForApprovalProcessesNotifications:
        userNotificationsSettings.enabledChannelsForApprovalProcessesNotifications ?? [],
      enabledChannelsForRequestsNotifications:
        userNotificationsSettings.enabledChannelsForRequestsNotifications ?? [],
      enabledChannelsForReportsNotifications:
        userNotificationsSettings.enabledChannelsForReportsNotifications ?? [],
    }
    setChannels(seeded)
    setOriginalChannels(seeded)
  }, [userNotificationsSettings])

  const channelsChanged = useMemo(
    () => JSON.stringify(originalChannels) !== JSON.stringify(channels),
    [channels, originalChannels]
  )

  //#endregion

  function changePassword(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault()
    OIDC_CLIENT.createSigninRequest({}).then((r) => {
      const signinUrl = new URL(r.url.replace('/connect/authorize', '/connect/authorize/callback'))
      const changePassUrl = `${Constants.stsAuthority}changepassword?ReturnUrl=${encodeURIComponent(
        signinUrl.pathname + signinUrl.search
      )}`
      window.location.href = changePassUrl
    })
  }

  function toggleConsent() {
    const newConsent = !consent
    if (newConsent) {
      window.MatomoConsent.consentGiven()
    } else {
      window.MatomoConsent.consentRevoked()
    }
    setConsent(newConsent)
  }

  async function save(e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    //! TODO : trycatch with error handling
    e?.preventDefault()
    if (selectedLang !== i18n.language) {
      i18n.changeLanguage(selectedLang)
      FrontendSettings.set(FrontendSettingsKeys.lang, selectedLang)
    }
    if (hasProfile && form) {
      patchCurrentUser(form)
      if (channelsChanged && canManageNotifs) {
        const model = buildSettingsInputModel(channels, userNotificationsSettings)
        if (model) await putNotificationSettings(model)
      }
    }
    goBack()
  }
  function goBack(e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e?.preventDefault()
    history.goBack()
  }
  return (
    <>
      <form className="popUserArea userPrefArea">
        <a onClick={() => goBack()} className="closeBtn">
          <i className="icon-close" />
        </a>
        <div className="row userPref">
          <div className="col-12">
            <div className="popTitle">
              <div>
                <i className="icon-user" />
                {t('USER_INFORMATION')}
              </div>
            </div>
            <div className="row">
              <div className="col-4">
                <label>{t('FIRST_NAME')}</label>
                {firstName}
              </div>
              <div className="col-4">
                <label>{t('LAST_NAME')}</label>
                {lastName}
              </div>
              <div className="col-4">
                <label>{t('PREFERRED_LANGUAGE')}</label>
                <div className="selectArea">
                  <select onChange={(e) => setSelectedLang(e.target.value)}>
                    <option selected={true} disabled={true}>
                      {t('CHOOSE_YOUR_PREFERRED_LANGUAGE')}
                    </option>
                    {Object.keys(langLocaleMap).map((el) => (
                      <option key={el} selected={el === selectedLang} value={el}>
                        {t(langTranslationMap[el as keyof typeof langTranslationMap])}
                      </option>
                    ))}
                  </select>
                  <i className="icon-bottom" />
                </div>
              </div>
              <div className="col-4">
                <label>{t('EMAIL')}</label>
                {user?.email}
              </div>
              {hasProfile && (
                <>
                  <div className="col-4">
                    <label>{t('PHONE')}</label>
                    <div className="input-area">
                      <input
                        type="text"
                        name={`no-autofill-${Date.now()}`}
                        autoComplete="none"
                        list="autocompleteOff"
                        className="transparent"
                        value={form?.phoneNumber || ''}
                        placeholder={t('INSERT_YOU_PHONE_NUMBER')}
                        onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-4">
                    <label>{t('MOBILEPHONE')}</label>
                    <div className="input-area">
                      <input
                        type="text"
                        name={`no-autofill-${Date.now()}`}
                        autoComplete="none"
                        list="autocompleteOff"
                        className="transparent"
                        value={form?.mobilePhoneNumber || ''}
                        placeholder={t('INSERT_YOU_MOBILE_PHONE_NUMBER')}
                        onChange={(e) => setForm({ ...form, mobilePhoneNumber: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {hasProfile && !user?.isGuest && (
            <div className="col-12 settings-section">
              <div className="roles">
                <div className="popTitle">
                  <div>
                    <i className="icon-key" />
                    {t('AUTH_SETTINGS')}
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="settings-row-group">
                      <div className="settings-row">
                        <div>
                          <button className="Ar" onClick={changePassword}>
                            {t('CHANGE_PASSWORD')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="settings-row-group">
                      <div className="settings-row">
                        <div>{t('2FA')}</div>
                        <div>
                          <div className="switch">
                            <input
                              type="checkbox"
                              name="Enabled"
                              checked={form?.enable2Fa ?? false}
                              onChange={(e) => setForm({ ...form, enable2Fa: e.target.checked })}
                            />
                            <span className="slider round" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="consent-container">
            <div className="consent-prompts">
              {PROMPTS.YouMayOptOut2}
              <br />
              {PROMPTS.YouMayOptOut3}
            </div>
            <div className="input-container">
              <label htmlFor="trackVisits">{PROMPTS.UncheckToOptOut}</label>
              <input
                className="consent-input checkbox round"
                onClick={toggleConsent}
                id="trackVisits"
                type="checkbox"
                checked={!consent}
                readOnly
              />
            </div>
          </div>
          {hasProfile && <UserRolesAndPermissions user={user} tenant={tenant} />}
          {canManageNotifs && (
            <UserNotificationSettings
              channels={channels}
              onChannelsChange={setChannels}
              userPermissions={user?.permissions ?? []}
            />
          )}
          <div className="btnArea">
            <a className="btn archive" onClick={(e) => goBack(e)}>
              <i className="icon-close" /> {t('CANCEL')}
            </a>
            <a className="btn btnOk" onClick={(e) => save(e)}>
              <i className="icon-save" /> {t('SAVE')}
            </a>
          </div>
        </div>
      </form>
      <div className="blur" />
    </>
  )
}
